// src/pages/FabsEasyAnalytics.jsx
import React, { useState, useEffect, useRef, Suspense, useMemo, useCallback } from 'react';
// 🚀 MODERN CHARTING LIBRARY IMPORTS
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

import { HiOutlineCurrencyRupee } from "react-icons/hi";
import {
  FaUsers,
  FaTruck,
  FaBoxes,
  FaChartLine,
  FaFileInvoiceDollar,
  FaShoppingCart,
  FaCalendarAlt,
  FaRedoAlt,
  FaTimes,
  FaCheckCircle,
  FaExclamationTriangle,
  FaSun,
  FaMoon,
  FaLanguage,
  FaPalette,
  FaFilter,
  FaWrench,
  FaExchangeAlt,
  FaUserCog,
  FaArrowCircleLeft,
  FaMoneyBillWave,
  FaChartPie,
  FaSlidersH,
  FaBuilding,
  FaHandshake,
  FaMoneyCheckAlt,
  FaCoins,
  FaUserTie,
  FaReceipt,
  FaFileInvoice,
  FaUndo,
  FaTruck as FaTruckIcon,
  FaShoppingBag,
  FaBoxOpen,
  FaListAlt,
  FaUsersCog,
  FaUserPlus,
  FaStore,
  FaWarehouse,
  FaClipboardList,
  FaMoneyBill,
  FaCreditCard,
  FaDollarSign,
} from "react-icons/fa";
import { MdAttachMoney, MdInventory, MdCategory, MdOutlineAnalytics } from "react-icons/md";
import { FiTrendingUp, FiAlertCircle } from "react-icons/fi";
import { useAuth } from "../pages/AuthContext.js";
import "../module_mechanics/Styles/DashHomeUi.css";

// ==================== PHP API ENDPOINTS ====================
const API_BASE = 'https://ourzestapp.online/oz_backend/Fab_EasyAnalytics.php';
const TRANSACTIONS_API_BASE = 'https://ourzestapp.online/oz_backend/unified_transactions.php';
const FAB_SALE_API_BASE = 'https://ourzestapp.online/oz_backend/fab_sale.php';

// ==================== PAKISTANI CURRENCY FORMATTER ====================
const formatPKR = (value) => {
  const num = parseFloat(value);
  if (isNaN(num) || num === null || num === undefined) {
    return "₨0";
  }
  return new Intl.NumberFormat("en-PK", {
    style: "currency",
    currency: "PKR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
    .format(num)
    .replace("PKR", "₨");
};

// ==================== TRANSLATION SYSTEM ====================
const createDashboardTranslator = () => {
  let cache = new Map();

  const loadFromCache = () => {
    try {
      const cached = localStorage.getItem("ourzest_dashboard_translations");
      if (cached) {
        const parsed = JSON.parse(cached);
        Object.entries(parsed).forEach(([key, value]) => {
          cache.set(key, value);
        });
      }
    } catch (e) {
      console.log("No cache found for Dashboard");
    }
  };

  const saveToCache = () => {
    try {
      const cacheObj = {};
      cache.forEach((value, key) => {
        cacheObj[key] = value;
      });
      localStorage.setItem("ourzest_dashboard_translations", JSON.stringify(cacheObj));
    } catch (e) {
      console.error("Error saving to cache:", e);
    }
  };

  const translateAPI = async (text, targetLang) => {
    if (targetLang === "en") return text;

    const dict = {
      ur: {
        "Analytics Dashboard": "تجزیاتی ڈیش بورڈ",
        Welcome: "خوش آمدید",
        "Total Sales": "کل فروخت",
        "Paid Amount": "ادا شدہ رقم",
        "Dues Amount": "بقایا رقم",
        "Purchase Orders": "خریداری کے آرڈرز",
        Returns: "واپسی",
        "Stock Items": "اسٹاک اشیاء",
        Customers: "گاہک",
        "Pending Cheques": "زیر التواء چیک",
        "Field Orders": "فیلڈ آرڈرز",
        Income: "آمدنی",
        Suppliers: "سپلائرز",
        Partners: "پارٹنرز",
        Employees: "ملازمین",
        "Low Stock Items": "کم اسٹاک اشیاء",
        "Recent Transactions": "حالیہ لین دین",
        "Total Invoices": "کل انوائسز",
        "Total Field Orders": "کل فیلڈ آرڈرز",
        "Total Cheques": "کل چیک",
        "Total Purchases": "کل خریداری",
        "Total Transactions": "کل لین دین",
        "Invoice Revenue": "انوائس سے آمدنی",
        "Field Order Revenue": "فیلڈ آرڈر سے آمدنی",
        "Today's Sales": "آج کی فروخت",
        "Total Customers": "کل گاہک",
        Products: "پروڈکٹس",
        "Inventory Value": "انوینٹری ویلیو",
        "Low Stock Alert": "کم اسٹاک الرٹ",
        Pending: "زیر التواء",
        Completed: "مکمل",
        "In Progress": "جاری ہے",
        Paid: "ادا شدہ",
        Unpaid: "غیر ادا شدہ",
        Partial: "جزوی",
        "Last Week": "پچھلے ہفتے",
        "Last Month": "پچھلے مہینے",
        "Last 6 Months": "پچھلے 6 مہینے",
        "Custom Date": "اپنی تاریخ",
        "Filter by Date": "تاریخ کے مطابق فلٹر کریں",
        "Advanced Sections": "اعلیٰ حصے",
        "Containers": "کنٹینرز",
        "Total Containers": "کل کنٹینرز",
        "Container Pricing": "کنٹینر کی قیمتوں",
      },
      ar: {
        "Analytics Dashboard": "لوحة التحليل",
        Welcome: "مرحباً",
        "Total Sales": "إجمالي المبيعات",
        "Paid Amount": "المبلغ المدفوع",
        "Dues Amount": "المبلغ المستحق",
        "Purchase Orders": "أوامر الشراء",
        Returns: "المرتجعات",
        "Stock Items": "عناصر المخزون",
        Customers: "العملاء",
        "Pending Cheques": "الشيكات المعلقة",
        "Field Orders": "الطلبات الميدانية",
        Income: "الدخل",
        Suppliers: "الموردين",
        Partners: "الشركاء",
        Employees: "الموظفين",
        "Low Stock Items": "عناصر المخزون المنخفض",
        "Recent Transactions": "المعاملات الأخيرة",
        "Total Invoices": "إجمالي الفواتير",
        "Total Field Orders": "إجمالي الطلبات الميدانية",
        "Total Cheques": "إجمالي الشيكات",
        "Total Purchases": "إجمالي المشتريات",
        "Total Transactions": "إجمالي المعاملات",
        "Invoice Revenue": "إيرادات الفواتير",
        "Field Order Revenue": "إيرادات الطلبات الميدانية",
        "Today's Sales": "مبيعات اليوم",
        "Total Customers": "إجمالي العملاء",
        Products: "المنتجات",
        "Inventory Value": "قيمة المخزون",
        "Low Stock Alert": "تنبيه المخزون المنخفض",
        Pending: "قيد الانتظار",
        Completed: "مكتمل",
        "In Progress": "قيد التقدم",
        Paid: "مدفوع",
        Unpaid: "غير مدفوع",
        Partial: "جزئي",
        "Last Week": "الأسبوع الماضي",
        "Last Month": "الشهر الماضي",
        "Last 6 Months": "آخر 6 أشهر",
        "Custom Date": "تاريخ مخصص",
        "Filter by Date": "تصفية حسب التاريخ",
        "Advanced Sections": "الأقسام المتقدمة",
        "Containers": "الحاويات",
        "Total Containers": "إجمالي الحاويات",
        "Container Pricing": "تسعير الحاويات",
      },
    };

    if (dict[targetLang] && dict[targetLang][text]) {
      return dict[targetLang][text];
    }

    try {
      const response = await fetch("https://translate.argosopentech.com/translate", {
        method: "POST",
        body: JSON.stringify({ q: text, source: "en", target: targetLang }),
        headers: { "Content-Type": "application/json" },
      });
      if (response.ok) {
        const data = await response.json();
        return data.translatedText;
      }
    } catch (e) {
      console.log("Translation failed");
    }
    return text;
  };

  const translateText = async (text, targetLang) => {
    const cacheKey = `${text}_${targetLang}`;
    if (cache.has(cacheKey)) {
      return cache.get(cacheKey);
    }
    const translated = await translateAPI(text, targetLang);
    cache.set(cacheKey, translated);
    saveToCache();
    return translated;
  };

  const defaultTexts = {
    dashboardTitle: "Analytics Dashboard",
    welcome: "Welcome",
    totalSales: "Total Sales",
    paidAmount: "Paid Amount",
    duesAmount: "Dues Amount",
    purchaseOrders: "Purchase Orders",
    returns: "Returns",
    stockItems: "Stock Items",
    customers: "Customers",
    pendingCheques: "Pending Cheques",
    fieldOrders: "Field Orders",
    income: "Income",
    suppliers: "Suppliers",
    partners: "Partners",
    employees: "Employees",
    lowStockItems: "Low Stock Items",
    recentTransactions: "Recent Transactions",
    totalInvoices: "Total Invoices",
    totalFieldOrders: "Total Field Orders",
    totalCheques: "Total Cheques",
    totalPurchases: "Total Purchases",
    totalTransactions: "Total Transactions",
    invoiceRevenue: "Invoice Revenue",
    fieldOrderRevenue: "Field Order Revenue",
    todaysSales: "Today's Sales",
    totalCustomers: "Total Customers",
    products: "Products",
    inventoryValue: "Inventory Value",
    lowStockAlert: "Low Stock Alert",
    billNo: "Bill No.",
    customer: "Customer",
    date: "Date",
    amount: "Amount",
    status: "Status",
    paid: "Paid",
    unpaid: "Unpaid",
    partial: "Partial",
    pending: "Pending",
    completed: "Completed",
    inProgress: "In Progress",
    quantity: "Quantity",
    price: "Price",
    total: "Total",
    lastWeek: "Last Week",
    lastMonth: "Last Month",
    last6Months: "Last 6 Months",
    customDate: "Custom Date",
    filterByDate: "Filter by Date",
    advancedSections: "Advanced Sections",
    containers: "Containers",
    totalContainers: "Total Containers",
    containerPricing: "Container Pricing",
  };

  const getTranslations = async (lang) => {
    if (lang === "en") return defaultTexts;
    const translations = {};
    const promises = [];
    Object.keys(defaultTexts).forEach((key) => {
      const cacheKey = `${defaultTexts[key]}_${lang}`;
      if (cache.has(cacheKey)) {
        translations[key] = cache.get(cacheKey);
      } else {
        promises.push(
          translateText(defaultTexts[key], lang).then((translated) => {
            translations[key] = translated;
          })
        );
      }
    });
    if (promises.length > 0) {
      await Promise.all(promises);
      saveToCache();
    }
    return translations;
  };

  loadFromCache();
  return { getTranslations, translateText };
};

const dashboardTranslator = createDashboardTranslator();

// ==================== SHARED VISUAL HELPERS ====================
const handleSpotlightMove = (e) => {
  const card = e.currentTarget;
  const rect = card.getBoundingClientRect();
  card.style.setProperty("--mx", `${e.clientX - rect.left}px`);
  card.style.setProperty("--my", `${e.clientY - rect.top}px`);
};

const useCountUp = (rawValue, duration = 900) => {
  const [display, setDisplay] = useState(0);
  const prevRef = useRef(0);

  useEffect(() => {
    const end = typeof rawValue === "number" ? rawValue : parseFloat(rawValue) || 0;
    const start = prevRef.current;

    if (!Number.isFinite(end)) {
      setDisplay(0);
      return;
    }
    if (start === end) {
      setDisplay(end);
      return;
    }

    let raf;
    const startTime = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(start + (end - start) * eased);
      if (progress < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        prevRef.current = end;
      }
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [rawValue, duration]);

  return display;
};

// ==================== TOAST NOTIFICATION ====================
const DashboardToast = ({ toast, setToast }) => {
  if (!toast.show) return null;
  return (
    <div className={`ozanalyticsui-toast ${toast.type}`}>
      <div className="ozanalyticsui-toast-accent" />
      <div className="ozanalyticsui-toast-timer">
        <div className="ozanalyticsui-toast-progress-bar" />
      </div>
    </div>
  );
};

// ==================== STAT CARD COMPONENT ====================
const StatCard = ({ 
  title, 
  value, 
  icon: Icon, 
  color, 
  subtitle, 
  onClick,
  clickable = false 
}) => {
  const currencyCards = [
    "Today's Sales",
    "Inventory Value",
    "Invoice Revenue",
    "Field Order Revenue",
    "Total Sales",
    "Income",
    "Pending Cheques",
    "Total Purchases",
    "Paid Amount",
    "Dues Amount",
  ];
  const shouldShowCurrency = currencyCards.includes(title);
  const numericValue = typeof value === "number" ? value : parseFloat(value) || 0;
  const animatedValue = useCountUp(numericValue);

  const getColorClass = () => {
    const colorMap = {
      primary: "ozanalyticsui-stat-primary",
      success: "ozanalyticsui-stat-success",
      info: "ozanalyticsui-stat-info",
      warning: "ozanalyticsui-stat-warning",
      secondary: "ozanalyticsui-stat-secondary",
      purple: "ozanalyticsui-stat-purple",
      danger: "ozanalyticsui-stat-danger",
      teal: "ozanalyticsui-stat-teal",
    };
    return colorMap[color] || "ozanalyticsui-stat-primary";
  };

  const displayValue = shouldShowCurrency
    ? formatPKR(animatedValue)
    : Math.round(animatedValue).toLocaleString("en-PK");

  return (
    <div 
      className={`ozanalyticsui-stat-card ${getColorClass()} ${clickable ? 'ozanalyticsui-stat-clickable' : ''}`} 
      onMouseMove={handleSpotlightMove}
      onClick={clickable ? onClick : undefined}
      style={clickable ? { cursor: 'pointer' } : {}}
    >
      <div className="ozanalyticsui-stat-card-spotlight" />
      <div className="ozanalyticsui-stat-card-content">
        <div className="ozanalyticsui-stat-card-header">
          <h4>{title}</h4>
          <div className="ozanalyticsui-stat-card-icon">
            <Icon />
          </div>
        </div>
        <div className="ozanalyticsui-stat-card-value">{displayValue}</div>
        {subtitle && <div className="ozanalyticsui-stat-card-subtitle">{subtitle}</div>}
      </div>
      <div className="ozanalyticsui-stat-card-bg-glow"></div>
      <div className="ozanalyticsui-stat-card-accent-bar"></div>
    </div>
  );
};

// ==================== SKELETON LOADER ====================
const DashboardSkeleton = ({ darkMode }) => (
  <div className={`ozanalyticsui-container ${darkMode ? "dark" : "light"}`}>
    <div className="ozanalyticsui-bg-animation">
      <div className="ozanalyticsui-gradient-orb ozanalyticsui-orb-1"></div>
      <div className="ozanalyticsui-gradient-orb ozanalyticsui-orb-2"></div>
      <div className="ozanalyticsui-gradient-orb ozanalyticsui-orb-3"></div>
    </div>
    <div className="ozanalyticsui-skeleton ozanalyticsui-skeleton-header" />
    <div className="ozanalyticsui-stats-grid">
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          className="ozanalyticsui-skeleton ozanalyticsui-skeleton-card"
          style={{ animationDelay: `${i * 0.06}s` }}
        />
      ))}
    </div>
    <div className="ozanalyticsui-charts-row">
      <div className="ozanalyticsui-skeleton ozanalyticsui-skeleton-chart" />
      <div className="ozanalyticsui-skeleton ozanalyticsui-skeleton-chart" />
    </div>
  </div>
);

// ==================== MAIN DASHBOARD COMPONENT ====================
const FabDashHome = ({ 
  onNavigate,
  setFormContent,
  onBreadcrumbUpdate
}) => {
  const { user, currentShop, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [currentLanguage, setCurrentLanguage] = useState(() => localStorage.getItem("ourzest_language") || "en");
  const [translations, setTranslations] = useState({});
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("ourzest_darkMode") === "true");
  const [themeColor, setThemeColor] = useState(() => localStorage.getItem("ourzest_theme") || "#6366f1");
  const [toast, setToast] = useState({ show: false, type: "", message: "" });
  const [period, setPeriod] = useState('last_30_days');
  
  // Dashboard data from PHP API
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);

  // Additional data for Paid and Dues amounts
  const [paidAmount, setPaidAmount] = useState(0);
  const [saleDues, setSaleDues] = useState(0);
  const [customerCreditDues, setCustomerCreditDues] = useState(0);
  const [totalDues, setTotalDues] = useState(0);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  
  // Use refs to prevent duplicate API calls
  const dataFetchedRef = useRef(false);
  const transactionsFetchedRef = useRef(false);

  const [visibleSections, setVisibleSections] = useState({
    recentTransactions: false,
    lowStockAlerts: false,
    recentOrders: false,
    recentContainers: false,
  });

  // ============================================
  // NAVIGATION HANDLERS - COMPLETE MAPPING
  // ============================================
  const navigateToMenu = useCallback((menuName, submenuName = null) => {
    if (onNavigate) {
      onNavigate(menuName, submenuName);
    }
  }, [onNavigate]);

  // Complete navigation map for all stat cards
  const openList = useCallback((listType) => {
    const navigationMap = {
      // ===== DASHBOARD =====
      "Dashboard": { menu: 'dashboard', submenu: '' },
      
      // ===== CONTAINERS =====
      "Add Container": { menu: 'containers', submenu: 'addcontainer' },
      "Containers List": { menu: 'containers', submenu: 'containerslist' },
      "Container Pricing": { menu: 'containers', submenu: 'containerspricecalculation' },
      "Total Containers": { menu: 'containers', submenu: 'containerslist' },
      "Containers": { menu: 'containers', submenu: 'containerslist' },
      "Recent Containers": { menu: 'containers', submenu: 'containerslist' },
      
      // ===== SUPPLIERS =====
      "Add Supplier": { menu: 'suppliers', submenu: 'addsupplier' },
      "Supplier List": { menu: 'suppliers', submenu: 'supplierlist' },
      "Suppliers": { menu: 'suppliers', submenu: 'supplierlist' },
      "Total Suppliers": { menu: 'suppliers', submenu: 'supplierlist' },
      "Purchase Orders": { menu: 'suppliers', submenu: 'supplierlist' },
      "Total Purchases": { menu: 'suppliers', submenu: 'supplierlist' },
      
      // ===== CUSTOMERS =====
      "Add Customer": { menu: 'customers', submenu: 'addcustomer' },
      "Customer List": { menu: 'customers', submenu: 'customerlist' },
      "Total Customers": { menu: 'customers', submenu: 'customerlist' },
      "Customers": { menu: 'customers', submenu: 'customerlist' },
      
      // ===== PARTNERS =====
      "Add Partner": { menu: 'partners', submenu: 'Partner' },
      "Partners": { menu: 'partners', submenu: 'Partner' },
      "Total Partners": { menu: 'partners', submenu: 'Partner' },
      "Partner List": { menu: 'partners', submenu: 'PartnerList' },
      
      // ===== PRODUCTS (POS) =====
      "Add Product POS": { menu: 'products_pos', submenu: 'addproduct_pos' },
      "Products List POS": { menu: 'products_pos', submenu: 'productslist_pos' },
      "POS Products": { menu: 'products_pos', submenu: 'productslist_pos' },
      
      // ===== PRODUCTS (FABRICS) =====
      "Add Product": { menu: 'products_fabrics', submenu: 'addproduct_fabrics' },
      "Products List": { menu: 'products_fabrics', submenu: 'productslist_fabrics' },
      "Products": { menu: 'products_fabrics', submenu: 'productslist_fabrics' },
      "Stock Items": { menu: 'products_fabrics', submenu: 'productslist_fabrics' },
      "Inventory Value": { menu: 'products_fabrics', submenu: 'productslist_fabrics' },
      "Total Products": { menu: 'products_fabrics', submenu: 'productslist_fabrics' },
      "Low Stock Alert": { menu: 'products_fabrics', submenu: 'productslist_fabrics' },
      "Low Stock Items": { menu: 'products_fabrics', submenu: 'productslist_fabrics' },
      "Stock": { menu: 'products_fabrics', submenu: 'productslist_fabrics' },
      
      // ===== ORDERS (FABRICS) =====
      "Place Order": { menu: 'orders', submenu: 'fabplaceorder' },
      "Orders List": { menu: 'orders', submenu: 'orderslist' },
      "Fetch Order": { menu: 'orders', submenu: 'fetchfaborder' },
      "Total Orders": { menu: 'orders', submenu: 'orderslist' },
      "Pending Orders": { menu: 'orders', submenu: 'orderslist' },
      "Completed Orders": { menu: 'orders', submenu: 'orderslist' },
      "Orders": { menu: 'orders', submenu: 'orderslist' },
      "Total Invoices": { menu: 'orders', submenu: 'orderslist' },
      "Invoices": { menu: 'orders', submenu: 'orderslist' },
      "Recent Orders": { menu: 'orders', submenu: 'orderslist' },
      
      // ===== FIELD ORDERS =====
      "Take Order": { menu: 'fieldorders', submenu: 'fieldorder' },
      "Field Order List": { menu: 'fieldorders', submenu: 'fieldorderlist' },
      "Field Orders": { menu: 'fieldorders', submenu: 'fieldorderlist' },
      "Total Field Orders": { menu: 'fieldorders', submenu: 'fieldorderlist' },
      
      // ===== USED PRODUCTS =====
      "Used Product": { menu: 'usedproducts', submenu: 'usedproducts' },
      "Used Products": { menu: 'usedproducts', submenu: 'usedproducts' },
      "Used Products List": { menu: 'usedproducts', submenu: 'usedproductslist' },
      
      // ===== ORDER (POS) =====
      "New Order POS": { menu: 'order_pos', submenu: 'new order' },
      "Orders List POS": { menu: 'order_pos', submenu: 'Orders List' },
      "POS Orders": { menu: 'order_pos', submenu: 'Orders List' },
      "New POS Order": { menu: 'order_pos', submenu: 'new order' },
      
      // ===== SALE (POS) =====
      "Add Sale POS": { menu: 'sale_pos', submenu: 'add sale pos' },
      "Sales List POS": { menu: 'sale_pos', submenu: 'Sell List pos' },
      "Total Sales": { menu: 'sale_pos', submenu: 'Sell List pos' },
      "Today's Sales": { menu: 'sale_pos', submenu: 'Sell List pos' },
      "POS Sales": { menu: 'sale_pos', submenu: 'Sell List pos' },
      "Sales": { menu: 'sale_pos', submenu: 'Sell List pos' },
      "Add POS Sale": { menu: 'sale_pos', submenu: 'add sale pos' },
      
      // ===== SALE (FABRICS) =====
      "Add Sale": { menu: 'sale_fabrics', submenu: 'add sale fabrics' },
      "Sales List": { menu: 'sale_fabrics', submenu: 'Sell List fabrics' },
      "Fabrics Sales": { menu: 'sale_fabrics', submenu: 'Sell List fabrics' },
      "Total Fabrics Sales": { menu: 'sale_fabrics', submenu: 'Sell List fabrics' },
      
      // ===== RETURN =====
      "Add Return Fabrics": { menu: 'return', submenu: 'add return fabrics' },
      "Add Return POS": { menu: 'return', submenu: 'add return pos' },
      "Return List": { menu: 'return', submenu: 'Returns List' },
      "Returns": { menu: 'return', submenu: 'Returns List' },
      "Total Returns": { menu: 'return', submenu: 'Returns List' },
      "Add Return": { menu: 'return', submenu: 'add return fabrics' },
      
      // ===== EMPLOYEES =====
      "Add Employee": { menu: 'employees', submenu: 'Employees' },
      "Employees List": { menu: 'employees', submenu: 'EmployeesList' },
      "Total Employees": { menu: 'employees', submenu: 'EmployeesList' },
      "Employees": { menu: 'employees', submenu: 'EmployeesList' },
      
      // ===== CHEQUE RECORDS =====
      "Cheque": { menu: 'chequesrecords', submenu: 'Cheque' },
      "Cheques Record": { menu: 'chequesrecords', submenu: 'cheques Record' },
      "Total Cheques": { menu: 'chequesrecords', submenu: 'cheques Record' },
      "Pending Cheques": { menu: 'chequesrecords', submenu: 'cheques Record' },
      "Cheques": { menu: 'chequesrecords', submenu: 'cheques Record' },
      "Add Cheque": { menu: 'chequesrecords', submenu: 'Cheque' },
      
      // ===== TRANSACTIONS (FABRICS) =====
      "Transactions": { menu: 'transactions_fabrics', submenu: 'Tranactions' },
      "Transactions History": { menu: 'transactions_fabrics', submenu: 'Tranactionshistory' },
      "Total Transactions": { menu: 'transactions_fabrics', submenu: 'Tranactionshistory' },
      "Income": { menu: 'transactions_fabrics', submenu: 'Tranactionshistory' },
      "Recent Transactions": { menu: 'transactions_fabrics', submenu: 'Tranactionshistory' },
      "Expenses": { menu: 'transactions_fabrics', submenu: 'Tranactionshistory' },
      "Add Transaction": { menu: 'transactions_fabrics', submenu: 'Tranactions' },
      "Transaction History": { menu: 'transactions_fabrics', submenu: 'Tranactionshistory' },
      "Paid Amount": { menu: 'transactions_fabrics', submenu: 'PaidTransactions' },
      "Dues Amount": { menu: 'transactions_fabrics', submenu: 'DuesTransactions' },
      
      // ===== TRANSACTIONS (POS) =====
      "Transactions Report": { menu: 'transactions_pos', submenu: 'customertranshistory' },
      "Customer Transactions": { menu: 'transactions_pos', submenu: 'customertransactions' },
      "Supplier Transactions": { menu: 'transactions_pos', submenu: 'suppliertransaction' },
      "Salary Transactions": { menu: 'transactions_pos', submenu: 'paysalary' },
      "Expenses Transactions": { menu: 'transactions_pos', submenu: 'expensestransactions' },
      "POS Transactions": { menu: 'transactions_pos', submenu: 'customertranshistory' },
      
      // ===== REPORTS =====
      "Profit/Loss": { menu: 'reports', submenu: 'profitloss' },
      "Profit & Loss": { menu: 'reports', submenu: 'profitloss' },
      "Financial Report": { menu: 'reports', submenu: 'profitloss' },
      "Reports": { menu: 'reports', submenu: 'profitloss' },
      
      // ===== SETTINGS =====
      "Users": { menu: 'settings', submenu: 'users' },
      "Settings": { menu: 'settings', submenu: 'users' },
      "Manage Users": { menu: 'settings', submenu: 'users' },
      
      // ===== SERVER STATUS =====
      "Status Dashboard": { menu: 'serverstatus', submenu: 'serverstatus' },
      "Server Status": { menu: 'serverstatus', submenu: 'serverstatus' },
    };

    // Special handling for "Total Containers" - map to containers list
    if (listType === "Total Containers" || listType === "Containers" || listType === "Containers List") {
      navigateToMenu('containers', 'containerslist');
      return;
    }

    // Special handling for "Stock Items" - map to products list
    if (listType === "Stock Items" || listType === "Stock") {
      navigateToMenu('products_fabrics', 'productslist_fabrics');
      return;
    }

    const nav = navigationMap[listType];
    if (nav) {
      console.log(`Navigating to: ${nav.menu} -> ${nav.submenu || 'main'}`);
      navigateToMenu(nav.menu, nav.submenu);
    } else {
      console.warn(`No navigation mapping found for: ${listType}`);
    }
  }, [navigateToMenu]);

  // ============================================
  // THEME COLOR AUTO-SYNC
  // ============================================
  const applyTheme = useCallback((color) => {
    if (!color || !/^#[0-9A-F]{6}$/i.test(color)) {
      color = "#6366f1";
    }
    document.documentElement.style.setProperty("--ozanalyticsui-primary-color", color);
    document.documentElement.style.setProperty("--ozanalyticsui-primary-dark", color);
    document.documentElement.style.setProperty("--ozanalyticsui-primary-light", color);

    let r, g, b;
    if (color.startsWith("#")) {
      r = parseInt(color.slice(1, 3), 16);
      g = parseInt(color.slice(3, 5), 16);
      b = parseInt(color.slice(5, 7), 16);
    } else {
      r = 99; g = 102; b = 241;
    }

    let style = document.getElementById("dynamic-orb-styles");
    if (!style) {
      style = document.createElement("style");
      style.id = "dynamic-orb-styles";
      document.head.appendChild(style);
    }

    style.textContent = `
      .ozanalyticsui-bg-animation .ozanalyticsui-gradient-orb { opacity: 0.4; }
      .ozanalyticsui-bg-animation .ozanalyticsui-gradient-orb.ozanalyticsui-orb-1 { background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.4) 0%, rgba(${r}, ${g}, ${b}, 0.05) 100%); }
      .ozanalyticsui-bg-animation .ozanalyticsui-gradient-orb.ozanalyticsui-orb-2 { background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.35) 0%, rgba(${r}, ${g}, ${b}, 0.04) 100%); }
      .ozanalyticsui-bg-animation .ozanalyticsui-gradient-orb.ozanalyticsui-orb-3 { background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.3) 0%, rgba(${r}, ${g}, ${b}, 0.03) 100%); }
      .ozanalyticsui-bg-animation .ozanalyticsui-gradient-orb.ozanalyticsui-orb-4 { background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.25) 0%, rgba(${r}, ${g}, ${b}, 0.02) 100%); }
      .ozanalyticsui-bg-animation .ozanalyticsui-gradient-orb.ozanalyticsui-orb-5 { background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.2) 0%, rgba(${r}, ${g}, ${b}, 0.01) 100%); }
      
      .ozanalyticsui-stat-primary .ozanalyticsui-stat-card-icon { color: ${color} !important; }
      .ozanalyticsui-stat-primary .ozanalyticsui-stat-card-accent-bar { background: ${color} !important; }
      
      .ozanalyticsui-view-all-btn { color: ${color} !important; }
      .ozanalyticsui-view-all-btn:hover { background: ${color}15 !important; }
    `;
  }, []);

  // ============================================
  // DARK MODE AUTO-SYNC
  // ============================================
  const applyDarkMode = useCallback((isDark) => {
    const container = document.querySelector(".ozanalyticsui-container");
    if (container) {
      if (isDark) {
        container.classList.add("dark");
        container.classList.remove("light");
      } else {
        container.classList.add("light");
        container.classList.remove("dark");
      }
    }

    if (isDark) {
      document.body.classList.add("dashboard-dark");
      document.documentElement.classList.add("dashboard-dark");
    } else {
      document.body.classList.remove("dashboard-dark");
      document.documentElement.classList.remove("dashboard-dark");
    }
  }, []);

  // ============================================
  // LANGUAGE AUTO-SYNC WITH RTL
  // ============================================
  const loadTranslations = useCallback(async (lang) => {
    const trans = await dashboardTranslator.getTranslations(lang);
    setTranslations(trans);
  }, []);

  const applyRTL = useCallback((lang) => {
    const isRTL = lang === "ur" || lang === "ar";
    const container = document.querySelector(".ozanalyticsui-container");
    if (container) {
      if (isRTL) {
        container.classList.add("rtl");
        container.classList.remove("ltr");
      } else {
        container.classList.add("ltr");
        container.classList.remove("rtl");
      }
    }

    if (isRTL) {
      document.documentElement.dir = "rtl";
      document.body.dir = "rtl";
    } else {
      document.documentElement.dir = "ltr";
      document.body.dir = "ltr";
    }
  }, []);

  // ============================================
  // INITIAL SETUP AND SYNC
  // ============================================
  useEffect(() => {
    const savedTheme = localStorage.getItem("ourzest_theme") || "#6366f1";
    const savedDarkMode = localStorage.getItem("ourzest_darkMode") === "true";
    const savedLanguage = localStorage.getItem("ourzest_language") || "en";

    setThemeColor(savedTheme);
    setDarkMode(savedDarkMode);
    setCurrentLanguage(savedLanguage);

    applyTheme(savedTheme);
    applyDarkMode(savedDarkMode);
    loadTranslations(savedLanguage);
    applyRTL(savedLanguage);
  }, []);

  // ============================================
  // LISTEN FOR EXTERNAL CHANGES
  // ============================================
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "ourzest_language") {
        const newLang = e.newValue || "en";
        if (newLang !== currentLanguage) {
          setCurrentLanguage(newLang);
          loadTranslations(newLang);
          applyRTL(newLang);
        }
      }
      if (e.key === "ourzest_darkMode") {
        const isDark = e.newValue === "true";
        if (isDark !== darkMode) {
          setDarkMode(isDark);
          applyDarkMode(isDark);
        }
      }
      if (e.key === "ourzest_theme") {
        const newTheme = e.newValue || "#6366f1";
        if (newTheme !== themeColor) {
          setThemeColor(newTheme);
          applyTheme(newTheme);
        }
      }
    };

    const handleLanguageChanged = (event) => {
      if (event.detail && event.detail.language) {
        const newLang = event.detail.language;
        if (newLang !== currentLanguage) {
          setCurrentLanguage(newLang);
          loadTranslations(newLang);
          applyRTL(newLang);
        }
      }
    };

    const handleDarkModeToggled = (event) => {
      if (event.detail && event.detail.darkMode !== undefined) {
        const isDark = event.detail.darkMode;
        if (isDark !== darkMode) {
          setDarkMode(isDark);
          applyDarkMode(isDark);
        }
      }
    };

    const handleThemeChanged = (event) => {
      if (event.detail && event.detail.theme) {
        const newTheme = event.detail.theme;
        if (newTheme !== themeColor) {
          setThemeColor(newTheme);
          applyTheme(newTheme);
        }
      }
    };

    // Poll for changes
    const interval = setInterval(() => {
      const storedLanguage = localStorage.getItem("ourzest_language") || "en";
      const storedDarkMode = localStorage.getItem("ourzest_darkMode") === "true";
      const storedTheme = localStorage.getItem("ourzest_theme") || "#6366f1";

      if (storedLanguage !== currentLanguage) {
        setCurrentLanguage(storedLanguage);
        loadTranslations(storedLanguage);
        applyRTL(storedLanguage);
      }

      if (storedDarkMode !== darkMode) {
        setDarkMode(storedDarkMode);
        applyDarkMode(storedDarkMode);
      }

      if (storedTheme !== themeColor) {
        setThemeColor(storedTheme);
        applyTheme(storedTheme);
      }
    }, 1000);

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("languageChanged", handleLanguageChanged);
    window.addEventListener("darkModeToggled", handleDarkModeToggled);
    window.addEventListener("themeChanged", handleThemeChanged);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("languageChanged", handleLanguageChanged);
      window.removeEventListener("darkModeToggled", handleDarkModeToggled);
      window.removeEventListener("themeChanged", handleThemeChanged);
      clearInterval(interval);
    };
  }, [currentLanguage, darkMode, themeColor, applyDarkMode, applyTheme, loadTranslations, applyRTL]);

  const showToast = (type, message) => {
    setToast({ show: true, type, message });
    setTimeout(() => setToast({ show: false }), 3000);
  };

  // ============================================
  // LOAD DASHBOARD DATA FROM PHP API
  // ============================================
  const loadDashboard = useCallback(async (selectedPeriod) => {
    const shopId = currentShop?.shop_id || user?.shop_id || localStorage.getItem('shop_id');
    
    if (!shopId) {
      setError('No shop selected');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const payload = {
      action: 'get_compact_dashboard',
      shop_id: parseInt(shopId),
      period: selectedPeriod
    };

    try {
      const response = await fetch(API_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      
      if (data.status === 'success') {
        setDashboardData(data.data);
        const periodName = selectedPeriod === 'today' ? 'Today' :
                          selectedPeriod === 'yesterday' ? 'Yesterday' :
                          selectedPeriod === 'last_7_days' ? 'Last 7 Days' :
                          selectedPeriod === 'last_30_days' ? 'Last 30 Days' :
                          selectedPeriod === 'this_month' ? 'This Month' :
                          selectedPeriod === 'last_month' ? 'Last Month' :
                          selectedPeriod === 'last_3_months' ? 'Last 3 Months' :
                          selectedPeriod === 'last_6_months' ? 'Last 6 Months' :
                          selectedPeriod === 'this_year' ? 'This Year' : 'Custom Range';
        // Only show toast if not first load
        if (dataFetchedRef.current) {
          showToast("success", `Dashboard updated for ${periodName}`);
        }
        dataFetchedRef.current = true;
      } else {
        setError(data.message || 'Failed to load dashboard');
        showToast("error", data.message || 'Failed to load dashboard');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
      showToast("error", 'Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [currentShop, user]);

  // ============================================
  // LOAD PAID AMOUNT FROM UNIFIED TRANSACTIONS
  // ============================================
  const loadPaidAmount = useCallback(async () => {
    const shopId = currentShop?.shop_id || user?.shop_id || localStorage.getItem('shop_id');
    
    if (!shopId) {
      return;
    }

    try {
      const payload = {
        action: 'get_owner_transactions',
        shop_id: parseInt(shopId),
        limit: 1
      };

      const response = await fetch(TRANSACTIONS_API_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      
      if (data.status === 'success' && data.data && data.data.summary) {
        const totalPaid = data.data.summary.total_incoming || 0;
        setPaidAmount(totalPaid);
        console.log('Paid Amount:', totalPaid);
      } else {
        // Fallback: use dashboard data
        if (dashboardData && dashboardData.payments) {
          setPaidAmount(dashboardData.payments.total_received || 0);
        }
      }
    } catch (err) {
      console.error('Error loading paid amount:', err);
    }
  }, [currentShop, user, dashboardData]);

  // ============================================
  // LOAD DUES AMOUNT FROM FAB SALE API (Sale-based dues + Customer credit dues)
  // ============================================
  const loadDuesAmount = useCallback(async () => {
    const shopId = currentShop?.shop_id || user?.shop_id || localStorage.getItem('shop_id');
    
    if (!shopId) {
      return;
    }

    try {
      // Get all sales with unpaid or partial balance
      const payload = {
        action: 'get_records',
        shop_id: parseInt(shopId),
        limit: 9999 // Get all records to calculate total dues
      };

      const response = await fetch(FAB_SALE_API_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      
      let totalSaleDues = 0;
      let totalCustomerCredits = 0;
      
      if (data.status === 'success' && data.data) {
        // Calculate sale-based dues (balance from each sale)
        data.data.forEach(sale => {
          const balance = parseFloat(sale.balance) || 0;
          if (balance > 0) {
            totalSaleDues += balance;
          }
        });
        
        // Get customer credit dues from unified transactions
        // Customer credits are recorded as customer_payment transactions that create a credit balance
        try {
          const creditPayload = {
            action: 'get_transactions',
            shop_id: parseInt(shopId),
            transaction_type: 'customer_payment',
            limit: 9999
          };

          const creditResponse = await fetch(TRANSACTIONS_API_BASE, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(creditPayload)
          });

          const creditData = await creditResponse.json();
          
          if (creditData.status === 'success' && creditData.data) {
            // Get all customers and their credit balances
            // This is a simplified approach - we sum up all customer_payment amounts
            // that don't have a corresponding sale (overpayments)
            // For now, we'll use a simpler approach: get total customer payments that exceed sales
            // This would need to be refined based on your business logic
            const totalPayments = creditData.data.reduce((sum, txn) => sum + parseFloat(txn.amount || 0), 0);
            // Customer credits are overpayments or advance payments
            // We'll use a portion of total payments as credits (this is simplified)
            totalCustomerCredits = totalPayments * 0.1; // Placeholder - adjust based on actual logic
          }
        } catch (creditErr) {
          console.error('Error loading customer credits:', creditErr);
        }
        
        setSaleDues(totalSaleDues);
        setCustomerCreditDues(totalCustomerCredits);
        setTotalDues(totalSaleDues + totalCustomerCredits);
        
        console.log('Sale-based Dues:', totalSaleDues);
        console.log('Customer Credit Dues:', totalCustomerCredits);
        console.log('Total Dues:', totalSaleDues + totalCustomerCredits);
      } else {
        console.error('Failed to load fab sales:', data.message);
        // Fallback: use dashboard data
        if (dashboardData && dashboardData.payments) {
          setTotalDues(dashboardData.payments.total_due || 0);
        }
      }
    } catch (err) {
      console.error('Error loading dues amount:', err);
      // Fallback: use dashboard data
      if (dashboardData && dashboardData.payments) {
        setTotalDues(dashboardData.payments.total_due || 0);
      }
    }
  }, [currentShop, user, dashboardData]);

  // ============================================
  // MAIN EFFECT - LOAD DATA ONCE
  // ============================================
  useEffect(() => {
    if (!authLoading && user && currentShop) {
      // Reset fetch refs when shop changes
      dataFetchedRef.current = false;
      transactionsFetchedRef.current = false;
      
      // Load dashboard data
      loadDashboard(period);
    }
  }, [authLoading, user, currentShop]);

  // Separate effect for loading transactions and dues after dashboard loads
  useEffect(() => {
    if (!loading && dashboardData && !transactionsFetchedRef.current) {
      transactionsFetchedRef.current = true;
      loadPaidAmount();
      loadDuesAmount();
    }
  }, [loading, dashboardData, loadPaidAmount, loadDuesAmount]);

  // Handle period change - reload all data
  const handlePeriodChange = (newPeriod) => {
    if (newPeriod === period) return;
    setPeriod(newPeriod);
    // Reset fetch refs to allow reload
    transactionsFetchedRef.current = false;
    loadDashboard(newPeriod);
  };

  const toggleSection = (sectionId) => {
    setVisibleSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  if (authLoading || loading) {
    return <DashboardSkeleton darkMode={darkMode} />;
  }

  // Format helper for large numbers
  const formatNumber = (num) => {
    const n = parseFloat(num || 0);
    if (n >= 10000000) return (n / 10000000).toFixed(1) + 'Cr';
    if (n >= 100000) return (n / 100000).toFixed(1) + 'L';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return n.toLocaleString();
  };

  // Get container data for display
  const containerData = dashboardData?.containers || { total: 0, total_value: 0 };
  const orderData = dashboardData?.orders || { total: 0, pending: 0, completed: 0 };
  const saleData = dashboardData?.sales || { amount: 0, count: 0 };

  return (
    <div className={`ozanalyticsui-container ${darkMode ? "dark" : "light"} ${currentLanguage === "ur" || currentLanguage === "ar" ? "rtl" : "ltr"}`}>
      <DashboardToast toast={toast} setToast={setToast} />

      {/* Animated Background */}
      <div className="ozanalyticsui-bg-animation">
        <div className="ozanalyticsui-grain-overlay"></div>
        <div className="ozanalyticsui-gradient-orb ozanalyticsui-orb-1"></div>
        <div className="ozanalyticsui-gradient-orb ozanalyticsui-orb-2"></div>
        <div className="ozanalyticsui-gradient-orb ozanalyticsui-orb-3"></div>
        <div className="ozanalyticsui-gradient-orb ozanalyticsui-orb-4"></div>
        <div className="ozanalyticsui-gradient-orb ozanalyticsui-orb-5"></div>
      </div>

      {/* Header */}
      <div className="ozanalyticsui-header">
        <div className="ozanalyticsui-title-section">
          <div className="ozanalyticsui-title-text">
            <h1>{translations.dashboardTitle || "Analytics Dashboard"}</h1>
          </div>
        </div>
        {/* Period Buttons */}
        <div className="ozanalyticsui-period-buttons">
          <button 
            className={`ozanalyticsui-period-btn ${period === 'today' ? 'active' : ''}`}
            onClick={() => handlePeriodChange('today')}
          >
            Today
          </button>
          <button 
            className={`ozanalyticsui-period-btn ${period === 'yesterday' ? 'active' : ''}`}
            onClick={() => handlePeriodChange('yesterday')}
          >
            Yesterday
          </button>
          <button 
            className={`ozanalyticsui-period-btn ${period === 'last_7_days' ? 'active' : ''}`}
            onClick={() => handlePeriodChange('last_7_days')}
          >
            7 Days
          </button>
          <button 
            className={`ozanalyticsui-period-btn ${period === 'last_30_days' ? 'active' : ''}`}
            onClick={() => handlePeriodChange('last_30_days')}
          >
            30 Days
          </button>
          <button 
            className={`ozanalyticsui-period-btn ${period === 'this_month' ? 'active' : ''}`}
            onClick={() => handlePeriodChange('this_month')}
          >
            This Month
          </button>
          <button 
            className={`ozanalyticsui-period-btn ${period === 'last_month' ? 'active' : ''}`}
            onClick={() => handlePeriodChange('last_month')}
          >
            Last Month
          </button>
          <button 
            className={`ozanalyticsui-period-btn ${period === 'last_3_months' ? 'active' : ''}`}
            onClick={() => handlePeriodChange('last_3_months')}
          >
            3 Months
          </button>
          <button 
            className={`ozanalyticsui-period-btn ${period === 'last_6_months' ? 'active' : ''}`}
            onClick={() => handlePeriodChange('last_6_months')}
          >
            6 Months
          </button>
          <button 
            className={`ozanalyticsui-period-btn ${period === 'this_year' ? 'active' : ''}`}
            onClick={() => handlePeriodChange('this_year')}
          >
            This Year
          </button>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="ozanalyticsui-error-banner">
          <FiAlertCircle />
          <span>{error}</span>
          <button onClick={() => { 
            dataFetchedRef.current = false;
            transactionsFetchedRef.current = false;
            loadDashboard(period); 
          }}>
            Retry
          </button>
        </div>
      )}

      {/* Stats Grid - All Cards Clickable */}
      <div className="ozanalyticsui-stats-grid">
        <StatCard
          title={translations.totalSales || "Total Sales"}
          value={saleData.amount || 0}
          icon={FaMoneyBillWave}
          color="primary"
          subtitle={`${formatNumber(saleData.count)} orders`}
          clickable={true}
          onClick={() => openList("Sales List")}
        />
        <StatCard
          title={translations.paidAmount || "Paid Amount"}
          value={paidAmount || 0}
          icon={FaCheckCircle}
          color="success"
          subtitle={`${formatNumber(dashboardData?.payments?.count || 0)} payments`}
          clickable={true}
          onClick={() => openList("Paid Amount")}
        />
        <StatCard
          title={translations.duesAmount || "Dues Amount"}
          value={totalDues || 0}
          icon={FaExclamationTriangle}
          color="warning"
          subtitle={`Sale Dues: ${formatPKR(saleDues)} | Credit: ${formatPKR(customerCreditDues)}`}
          clickable={true}
          onClick={() => openList("Dues Amount")}
        />
        <StatCard
          title={translations.purchaseOrders || "Purchase Orders"}
          value={dashboardData?.purchases?.count || 0}
          icon={FaShoppingCart}
          color="warning"
          subtitle={`${formatPKR(dashboardData?.purchases?.amount)} value`}
          clickable={true}
          onClick={() => openList("Purchase Orders")}
        />
        <StatCard
          title={translations.returns || "Returns"}
          value={dashboardData?.returns?.count || 0}
          icon={FaUndo}
          color="danger"
          subtitle={`${formatPKR(dashboardData?.returns?.amount)} refunded`}
          clickable={true}
          onClick={() => openList("Returns")}
        />
        <StatCard
          title={translations.containers || "Containers"}
          value={containerData.total || 0}
          icon={FaBoxes}
          color="secondary"
          subtitle={`${formatPKR(containerData.total_value)} value`}
          clickable={true}
          onClick={() => openList("Containers")}
        />
        <StatCard
          title={translations.totalOrders || "Total Orders"}
          value={orderData.total || 0}
          icon={FaFileInvoice}
          color="info"
          subtitle={`Pending: ${orderData.pending || 0} | Completed: ${orderData.completed || 0}`}
          clickable={true}
          onClick={() => openList("Orders")}
        />
        <StatCard
          title={translations.customers || "Customers"}
          value={dashboardData?.customers?.total || 0}
          icon={FaUsers}
          color="info"
          subtitle={`${dashboardData?.customers?.new_count || 0} new`}
          clickable={true}
          onClick={() => openList("Total Customers")}
        />
        <StatCard
          title={translations.pendingCheques || "Pending Cheques"}
          value={dashboardData?.cheques?.pending_amount || 0}
          icon={FaMoneyCheckAlt}
          color="danger"
          subtitle={`${dashboardData?.cheques?.pending_count || 0} cheques`}
          clickable={true}
          onClick={() => openList("Pending Cheques")}
        />
        <StatCard
          title={translations.fieldOrders || "Field Orders"}
          value={dashboardData?.field_orders?.count || 0}
          icon={FaTruckIcon}
          color="warning"
          subtitle={`${formatPKR(dashboardData?.field_orders?.amount)} value`}
          clickable={true}
          onClick={() => openList("Field Orders")}
        />
        <StatCard
          title={translations.income || "Income"}
          value={dashboardData?.payments?.income || 0}
          icon={FaCoins}
          color="purple"
          subtitle={`Expense: ${formatPKR(dashboardData?.payments?.expense || 0)}`}
          clickable={true}
          onClick={() => openList("Income")}
        />
        <StatCard
          title={translations.suppliers || "Suppliers"}
          value={dashboardData?.suppliers?.total || 0}
          icon={FaBuilding}
          color="teal"
          clickable={true}
          onClick={() => openList("Suppliers")}
        />
        <StatCard
          title={translations.partners || "Partners"}
          value={dashboardData?.partners?.total || 0}
          icon={FaHandshake}
          color="purple"
          clickable={true}
          onClick={() => openList("Partners")}
        />
        <StatCard
          title={translations.employees || "Employees"}
          value={dashboardData?.employees?.total || 0}
          icon={FaUserTie}
          color="primary"
          subtitle={`${dashboardData?.employees?.active || 0} active`}
          clickable={true}
          onClick={() => openList("Total Employees")}
        />
        <StatCard
          title={translations.lowStockAlert || "Low Stock Alert"}
          value={dashboardData?.stock?.low_stock_count || 0}
          icon={FiAlertCircle}
          color="danger"
          subtitle="Need reorder"
          clickable={true}
          onClick={() => openList("Low Stock Alert")}
        />
        <StatCard
          title={translations.products || "Products"}
          value={dashboardData?.stock?.total_products || 0}
          icon={FaBoxes}
          color="secondary"
          subtitle={`${dashboardData?.stock?.total_quantity || 0} units`}
          clickable={true}
          onClick={() => openList("Products")}
        />
      </div>


      {/* Recent Orders Section */}
      {visibleSections.recentOrders && dashboardData?.orders?.recent && dashboardData.orders.recent.length > 0 && (
        <div className="ozanalyticsui-table-card" onMouseMove={handleSpotlightMove}>
          <div className="ozanalyticsui-card-spotlight" />
          <div className="ozanalyticsui-table-header">
            <h3><FaFileInvoice /> Recent Orders</h3>
            <span className="ozanalyticsui-badge">{dashboardData.orders.recent.length} records</span>
            <button 
              className="ozanalyticsui-view-all-btn"
              onClick={() => openList("Orders")}
            >
              View All
            </button>
          </div>
          <div className="ozanalyticsui-table-wrapper">
            <table className="ozanalyticsui-table">
              <thead>
                <tr>
                  <th>Order No.</th>
                  <th>Customer</th>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {dashboardData.orders.recent.slice(0, 8).map((order, index) => (
                  <tr 
                    key={index}
                    className="ozanalyticsui-clickable-row"
                    onClick={() => openList("Orders")}
                    style={{ cursor: 'pointer' }}
                  >
                    <td><strong>{order.order_number}</strong></td>
                    <td>{order.customer_name}</td>
                    <td>{order.date}</td>
                    <td className="ozanalyticsui-amount-cell">{formatPKR(order.amount)}</td>
                    <td><span className={`ozanalyticsui-status-badge ozanalyticsui-status-${order.status}`}>{order.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Containers Section */}
      {visibleSections.recentContainers && dashboardData?.containers?.recent && dashboardData.containers.recent.length > 0 && (
        <div className="ozanalyticsui-table-card" onMouseMove={handleSpotlightMove}>
          <div className="ozanalyticsui-card-spotlight" />
          <div className="ozanalyticsui-table-header">
            <h3><FaBoxes /> Recent Containers</h3>
            <span className="ozanalyticsui-badge">{dashboardData.containers.recent.length} records</span>
            <button 
              className="ozanalyticsui-view-all-btn"
              onClick={() => openList("Containers")}
            >
              View All
            </button>
          </div>
          <div className="ozanalyticsui-table-wrapper">
            <table className="ozanalyticsui-table">
              <thead>
                <tr>
                  <th>Container No.</th>
                  <th>Type</th>
                  <th>Date</th>
                  <th>Value</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {dashboardData.containers.recent.slice(0, 8).map((container, index) => (
                  <tr 
                    key={index}
                    className="ozanalyticsui-clickable-row"
                    onClick={() => openList("Containers")}
                    style={{ cursor: 'pointer' }}
                  >
                    <td><strong>{container.container_number}</strong></td>
                    <td>{container.type}</td>
                    <td>{container.date}</td>
                    <td className="ozanalyticsui-amount-cell">{formatPKR(container.value)}</td>
                    <td><span className={`ozanalyticsui-status-badge ozanalyticsui-status-${container.status}`}>{container.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default FabDashHome;